import Pusher, { type Channel } from 'pusher-js';

interface PusherBindAllChannel extends Channel {
  bind_all(callback: (event: string, payload: unknown) => void): {
    global_callbacks: Array<(event: string, payload: unknown) => void>;
  };
}

interface SubscriptionRecord {
  count: number;
  subscription: PusherBindAllChannel;
}

const createPusherClient = () => {
  const config = window.clutter.config.pusher;
  const options: ConstructorParameters<typeof Pusher>[1] = { cluster: config.cluster };

  if (window.clutter.env() === 'test') {
    Object.assign(options, {
      wsHost: config.host,
      wsPort: config.port,
    });
  } else {
    Object.assign(options, { authEndpoint: '/pusher/auth' });
  }

  return new Pusher(config.key, options);
};

export const createPusherService = () => {
  const client = createPusherClient();
  const subscriptions: Record<string, SubscriptionRecord | null> = {};

  const getSubscription = (channel: string) => {
    subscriptions[channel] = subscriptions[channel] || {
      count: 0,
      subscription: client.subscribe(channel) as PusherBindAllChannel,
    };
    const record = subscriptions[channel]!;
    record.count += 1;
    return record.subscription;
  };

  const clearSubscription = (channel: string) => {
    const record = subscriptions[channel];
    if (!record) return;

    record.count -= 1;
    if (record.count <= 0) {
      client.unsubscribe(channel);
      subscriptions[channel] = null;
    }
  };

  return {
    client,
    subscribe(channel: string, callback: (event: string, payload: unknown) => void) {
      const subscription = getSubscription(channel);
      const { global_callbacks: globalCallbacks } = subscription.bind_all(callback);
      return () => {
        clearSubscription(channel);
        const callbackIndex = globalCallbacks.indexOf(callback);
        if (callbackIndex >= 0) {
          globalCallbacks.splice(callbackIndex, 1);
        }
      };
    },
  };
};
